"use client";

import { usePathname, useRouter } from "next/navigation";
import * as React from "react";

import Link from "next/link";

import { Dialog, DialogTitle } from "~/components/ui/dialog";
import { CART_PATH, CHECKOUT_PATH } from "~/config/storefront";
import { SUPPORTED_LANGUAGES } from "~/i18n/config";
import { useCurrentPath, useLocale, useTranslator } from "~/i18n/provider";
import { localeHref } from "~/i18n/routing";
// P11-23 · The consent-gated pipeline, not `~/lib/analytics`'s inert `track`.
// These three events were emitted into that stub since P8-04/P8-05A and were
// never recorded anywhere.
import { track } from "~/lib/analytics/client";
import { currencySymbol } from "~/lib/money";
import { cn } from "~/lib/utils";
import { setLanguagePreference } from "~/server/i18n/set-locale";
import { setMarketPreference } from "~/server/i18n/set-market";
import { useMarketPicker } from "~/components/storefront/market/market-picker-context";
import { reportMarketOverride } from "~/components/storefront/market/market-disclosure";
import { api } from "~/trpc/react";
import { useCartContext } from "~/lib/cart/use-cart";

/**
 * P11-19 · The market selector — "what am I reading, what will I pay in, and
 * where does it ship".
 *
 * An evolution of the P8-DB01 `LocaleSwitcher`, not a replacement for it: that
 * control was already in the header, already flag-free, and already treated
 * language as a navigation. What it lacked was a working region axis (a
 * disabled shell "pending the P8-06 cascade"), region in the trigger, and a
 * staged apply.
 *
 * ## Two apply models, on purpose (P11-DB01 D1)
 *
 * **Language applies on tap**, because it is a navigation: choosing it changes
 * the URL, and staging a URL change behind a button would mean the panel's
 * state and the address bar disagreeing until you pressed it.
 *
 * **Currency and region stage behind Apply**, and commit through ONE server
 * action. Applying them separately gives the shopper two reprices and, in
 * between, a page whose prices are converted into the new currency while still
 * carrying the old region's price list. The combined action rejects the whole
 * apply rather than half-committing, so that state is unreachable rather than
 * merely brief.
 *
 * ## Per-currency availability
 *
 * A currency whose rate is missing or stale is rendered disabled with the
 * reason, instead of the shipped behaviour of hiding every currency at once
 * because one of them was degraded.
 */

function languageLabel(language: string): string {
  return (
    SUPPORTED_LANGUAGES[language as keyof typeof SUPPORTED_LANGUAGES]
      ?.endonym ?? language.toUpperCase()
  );
}

/**
 * An axis with exactly one option: stated, not offered.
 *
 * P11-DB01 — "one enabled value → that axis renders as a static label, not a
 * control". A radio group of one is a control that cannot do anything, and it
 * invites a tap that changes nothing. An axis with NO options (its feature flag
 * off) renders nothing at all rather than an empty label.
 */
function StaticAxis({ label, value }: { label: string; value: string }) {
  return (
    <div className="mt-4">
      <p className="mb-2 text-xs font-semibold text-fg-muted">{label}</p>
      <div className="rounded-lg border border-border px-3 py-2.5 text-sm text-fg-muted">
        {value}
      </div>
    </div>
  );
}

/** Radio-style dot, shared by the currency and region groups. */
function Dot({ active }: { active: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "h-3 w-3 flex-none rounded-full border-2",
        active ? "border-[5px] border-secondary bg-surface" : "border-border",
      )}
    />
  );
}

/**
 * The picker dialog. Rendered ONCE, at the layout level — every pill is only a
 * trigger. See `market-picker-context.tsx` for why.
 */
export function MarketPicker() {
  const utils = api.useUtils();
  const locale = useLocale();
  const t = useTranslator();
  const router = useRouter();
  const pathname = usePathname();
  const barePath = useCurrentPath();
  const picker = useMarketPicker();
  // Read the cart rather than take it as a prop. It WAS a prop, and the layout
  // never passed it, so the reprice warning could not fire for anyone — the
  // kind of dead branch that looks implemented in review and does nothing in
  // production.
  const cart = useCartContext();
  const hasCart = (cart?.count ?? 0) > 0;

  const open = picker?.open ?? false;
  const setOpen = React.useCallback(
    (next: boolean) => {
      picker?.setOpen(next);
      // Hand focus back to whichever pill opened this. Without it the shopper
      // is dropped at the top of the document, which on mobile means scrolling
      // back to where they were.
      if (!next) picker?.lastTrigger.current?.focus();
    },
    [picker],
  );
  const [pendingLanguage, setPendingLanguage] = React.useState<string | null>(
    null,
  );
  const [applying, setApplying] = React.useState(false);
  const [applyError, setApplyError] = React.useState<string | null>(null);

  const languages = locale.enabledLanguages;
  const currencies = locale.marketCurrencies;
  const regions = locale.marketRegions;

  // P11-DB01 · Locked from checkout start. The server already pins both axes
  // for a checkout session — the region onto `Cart.pricingRegionId` and the
  // rate onto the `Payment` — so a switch here would not change what is being
  // charged; it would only make the header disagree with the quote the shopper
  // is looking at. Locking the control is how that stays true visibly, rather
  // than silently.
  const locked = barePath.startsWith(CHECKOUT_PATH);

  // Staged state — what the shopper has picked but not yet applied.
  const [draftCurrency, setDraftCurrency] = React.useState(locale.currency);
  const [draftRegion, setDraftRegion] = React.useState(locale.region ?? null);

  // Re-sync when the panel opens so a dismissed selection does not persist as
  // a stale draft into the next visit.
  React.useEffect(() => {
    if (open) {
      setDraftCurrency(locale.currency);
      setDraftRegion(locale.region ?? null);
    }
  }, [open, locale.currency, locale.region]);

  // P11-DB01 · A brand with nothing to choose between shows no control at all.
  // Generalised from P8's "one language AND one currency" to all three axes:
  // the rule is "no plural axis", so a single-language single-currency brand
  // that offers two regions now correctly gets a pill.
  const plural =
    languages.length > 1 || currencies.length > 1 || regions.length > 1;
  if (!plural) return null;

  const chooseLanguage = async (language: string) => {
    if (language === locale.language) {
      setOpen(false);
      return;
    }
    setPendingLanguage(language);
    track("I18N_LANGUAGE_CHANGED", {
      props: { from: locale.language, to: language },
    });

    // Persist first — the destination resolves its language from this cookie.
    await setLanguagePreference(language);

    const href = localeHref(barePath, {
      language,
      defaultLanguage: locale.defaultLanguage,
      hideDefault: locale.hideDefaultLocalePath,
    });
    const query = pathname.includes("?") ? "" : window.location.search;
    setOpen(false);
    setPendingLanguage(null);
    router.push(`${href}${query}`);
    router.refresh();
  };

  const currencyChanged = draftCurrency !== locale.currency;
  const regionChanged = (draftRegion ?? null) !== (locale.region ?? null);
  const dirty = currencyChanged || regionChanged;

  const apply = async () => {
    if (!dirty) {
      setOpen(false);
      return;
    }
    setApplying(true);
    if (currencyChanged) {
      track("I18N_CURRENCY_CHANGED", {
        props: { from: locale.currency, to: draftCurrency },
      });
    }
    if (regionChanged) {
      track("I18N_REGION_CHANGED", {
        props: { from: locale.region, to: draftRegion },
      });
    }
    // P11-23 · If a geo default was disclosed to this shopper and they are now
    // applying a different market, that is the disclosure being overridden —
    // the one observable signal that our country→region mapping was wrong.
    // Recorded here rather than on the banner's "Change" button, because
    // opening the picker and closing it again changes nothing.
    reportMarketOverride({
      currency: currencyChanged ? draftCurrency : null,
      region: regionChanged ? draftRegion : null,
    });

    setApplyError(null);
    try {
    const result = await setMarketPreference({
      ...(currencyChanged ? { currency: draftCurrency } : {}),
      ...(regionChanged && draftRegion ? { region: draftRegion } : {}),
    });

    if (!result.ok) { setApplyError("That choice is no longer available. Refresh the options and choose again."); return; }
    await Promise.all([utils.cart.get.invalidate(), utils.checkout.get.invalidate()]);
    setOpen(false);
    // `refresh`, not `push`: neither axis is in the URL. Every price is
    // projected server-side, so re-rendering the route reprices the page — one
    // refresh for both axes, which is the whole point of staging them.
    router.refresh();
    } catch { setApplyError("Your choice could not be saved. Please try again."); } finally { setApplying(false); }
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        ariaLabel={t("locale.panelTitle")}
        className="max-w-md p-0"
      >
        <div className="border-b border-border px-5 py-4">
          <DialogTitle>{t("locale.panelTitle")}</DialogTitle>
          <p className="mt-0.5 text-xs text-fg-muted">
            {t("locale.panelSubtitle")}
          </p>
        </div>

        {applyError && <p role="alert" className="px-5 py-2 text-sm">{applyError}</p>}
        <div className="px-5 py-4">
          {locked ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold text-fg">
                {t("locale.lockedTitle")}
              </p>
              <p className="text-sm text-fg-muted">
                {t("locale.lockedBody", { currency: locale.currency })}
              </p>
              <Link
                href={CART_PATH}
                onClick={() => setOpen(false)}
                className="text-sm font-semibold text-secondary underline underline-offset-2"
              >
                {t("locale.lockedCartLink")}
              </Link>
            </div>
          ) : (
            <>
              {/* LANGUAGE — a link list in behaviour: applies on tap, navigates. */}
              {languages.length > 1 ? (
                <fieldset>
                  <legend className="mb-2 text-xs font-semibold text-fg-muted">
                    {t("locale.languageLabel")}
                  </legend>
                  <div className="grid grid-cols-2 gap-2">
                    {languages.map((language) => {
                      const active = language === locale.language;
                      return (
                        <button
                          key={language}
                          type="button"
                          lang={language}
                          aria-current={active ? "true" : undefined}
                          disabled={pendingLanguage != null || applying}
                          onClick={() => void chooseLanguage(language)}
                          className={cn(
                            "flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]",
                            active
                              ? "border-secondary bg-secondary/10 font-semibold text-fg"
                              : "border-border text-fg hover:bg-surface-2",
                            (pendingLanguage != null || applying) &&
                              "opacity-60",
                          )}
                        >
                          <Dot active={active} />
                          {languageLabel(language)}
                          {pendingLanguage === language && (
                            <span className="ml-auto text-xs text-fg-muted">
                              {t("common.loading")}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
              ) : (
                <StaticAxis
                  label={t("locale.languageLabel")}
                  value={languageLabel(locale.language)}
                />
              )}

              {/* CURRENCY — staged. A single option renders as a label, not a
              control offering one choice. */}
              {currencies.length > 1 ? (
                <fieldset className="mt-4">
                  <legend className="mb-2 text-xs font-semibold text-fg-muted">
                    {t("locale.currencyLabel")}
                  </legend>
                  <div className="grid grid-cols-2 gap-2" role="radiogroup">
                    {currencies.map((option) => {
                      const active = option.code === draftCurrency;
                      return (
                        <button
                          key={option.code}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          // Choosing an unusable currency would show base prices
                          // anyway, so it is refused with a reason rather than
                          // silently ignored.
                          disabled={!option.selectable || applying}
                          title={
                            option.selectable
                              ? undefined
                              : t("locale.currencyUnavailable", {
                                  currency: option.code,
                                  base: locale.baseCurrency,
                                })
                          }
                          onClick={() => setDraftCurrency(option.code)}
                          className={cn(
                            "flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]",
                            active
                              ? "border-secondary bg-secondary/10 font-semibold text-fg"
                              : "border-border text-fg hover:bg-surface-2",
                            !option.selectable &&
                              "cursor-not-allowed opacity-50",
                          )}
                        >
                          <Dot active={active} />
                          {option.code}
                          {currencySymbol(option.code, locale.intlLocale) !==
                          option.code
                            ? ` ${currencySymbol(option.code, locale.intlLocale)}`
                            : ""}
                          {!option.selectable && (
                            <span className="ml-auto text-[11px] text-fg-muted">
                              {t("locale.ratesUnavailable")}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {/* Since P8-05B this is what the shopper is CHARGED in, not just
                  what they are shown. Say so where the choice is made. */}
                  <p className="mt-2 text-[11px] text-fg-muted">
                    {t("locale.currencyPayNote")}
                  </p>
                </fieldset>
              ) : currencies.length === 1 ? (
                <StaticAxis
                  label={t("locale.currencyLabel")}
                  value={locale.currency}
                />
              ) : null}

              {/* REGION — staged, alongside currency. */}
              {regions.length > 1 ? (
                <fieldset className="mt-4">
                  <legend className="mb-2 text-xs font-semibold text-fg-muted">
                    {t("locale.regionLabel")}
                  </legend>
                  <div className="grid grid-cols-2 gap-2" role="radiogroup">
                    {regions.map((region) => {
                      const active = region.code === draftRegion;
                      return (
                        <button
                          key={region.code}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          disabled={applying}
                          onClick={() => setDraftRegion(region.code)}
                          className={cn(
                            "flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]",
                            active
                              ? "border-secondary bg-secondary/10 font-semibold text-fg"
                              : "border-border text-fg hover:bg-surface-2",
                          )}
                        >
                          <Dot active={active} />
                          {region.name}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
              ) : regions.length === 1 ? (
                <StaticAxis
                  label={t("locale.regionLabel")}
                  value={regions[0]!.name}
                />
              ) : null}

              {/* The cart survives the change, but the totals will not look the
              same afterwards — say so before, not after. Gift cards are NOT
              mentioned here: stored value is base-anchored and deducted before
              conversion, so switching neither breaks nor fixes one. */}
              {hasCart && dirty ? (
                <p
                  role="status"
                  className="mt-4 rounded-md border border-border bg-surface-2 px-3 py-2 text-xs text-fg-muted"
                >
                  {t("locale.repriceNotice")}
                </p>
              ) : null}
            </>
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-border bg-surface-2 px-5 py-3">
          <p className="flex-1 text-[11px] leading-snug text-fg-muted">
            {locked ? "" : t("locale.persistNote")}
          </p>
          <button
            type="button"
            disabled={applying}
            onClick={() => void apply()}
            data-testid="market-apply"
            className="rounded-lg bg-secondary px-4 py-2 text-sm font-semibold text-secondary-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] disabled:opacity-60"
          >
            {applying
              ? t("locale.applyingMarket")
              : dirty && !locked
                ? t("common.apply")
                : t("common.done")}
          </button>
        </div>
      </Dialog>

      {/* Announce the change: the visible cue is the page re-rendering, which
          is not announced on its own. */}
      <span aria-live="polite" className="sr-only">
        {pendingLanguage
          ? t("locale.applyingLanguage", {
              language: languageLabel(pendingLanguage),
            })
          : applying
            ? t("locale.applyingMarket")
            : ""}
      </span>
    </>
  );
}
